import { Component, OnInit } from '@angular/core';
import {
  NotificationItem,
  NotificationService,
  NOTIFICATION_TYPE_CONFIG,
  getNotificationTypeConfig,
} from 'src/app/services/notification.service';

@Component({
  selector: 'app-notification-center',
  templateUrl: './notification-center.component.html',
  styleUrls: ['./notification-center.component.sass'],
})
export class NotificationCenterComponent implements OnInit {
  notifications: NotificationItem[] = [];
  total = 0;
  currentPage = 0;
  pageSize = 10;
  loading = false;

  selectedTypes: string[] = [];
  readStatus = 'all';
  selectedIds: string[] = [];

  readonly typeOptions = Object.keys(NOTIFICATION_TYPE_CONFIG).map((key) => ({
    label: NOTIFICATION_TYPE_CONFIG[key].label || key.replace(/_/g, ' '),
    value: key,
  }));

  readonly readStatusOptions = [
    { label: 'All', value: 'all' },
    { label: 'Unread', value: 'unread' },
    { label: 'Read', value: 'read' },
  ];

  constructor(private notificationService: NotificationService) {}

  ngOnInit(): void {
    this.load();
  }

  async load(): Promise<void> {
    this.loading = true;
    this.selectedIds = [];
    try {
      const isRead =
        this.readStatus === 'all'
          ? undefined
          : this.readStatus === 'unread'
          ? false
          : true;
      const { data, total } = await this.notificationService.getNotifications(
        this.currentPage,
        this.pageSize,
        this.selectedTypes.length ? this.selectedTypes : undefined,
        isRead,
      );
      this.notifications = data;
      this.total = total;
    } catch {
      // silently fail
    } finally {
      this.loading = false;
    }
  }

  onFilterChange(): void {
    this.currentPage = 0;
    this.load();
  }

  onPageChange(event: any): void {
    this.currentPage = event.page;
    this.pageSize = event.rows;
    this.load();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async onRowClick(n: NotificationItem): Promise<void> {
    if (!n.isRead) {
      try {
        await this.notificationService.markAsRead(n._id);
        n.isRead = true;
      } catch {}
    }
  }

  async onMarkAllAsRead(): Promise<void> {
    try {
      await this.notificationService.markAllAsRead();
      this.notifications.forEach((n) => (n.isRead = true));
    } catch {}
  }

  async onDismissSelected(): Promise<void> {
    const ids = [...this.selectedIds];
    try {
      await Promise.all(
        ids.map((id) => this.notificationService.deleteNotification(id)),
      );
      this.notifications = this.notifications.filter(
        (n) => !ids.includes(n._id),
      );
      this.total = Math.max(0, this.total - ids.length);
      this.selectedIds = [];
    } catch {}
  }

  async onDismiss(n: NotificationItem, event: Event): Promise<void> {
    event.stopPropagation();
    try {
      await this.notificationService.deleteNotification(n._id);
      this.notifications = this.notifications.filter((x) => x._id !== n._id);
      this.total = Math.max(0, this.total - 1);
      this.selectedIds = this.selectedIds.filter((id) => id !== n._id);
    } catch {}
  }

  clearFilters(): void {
    this.selectedTypes = [];
    this.readStatus = 'all';
    this.currentPage = 0;
    this.load();
  }

  get hasFilters(): boolean {
    return this.selectedTypes.length > 0 || this.readStatus !== 'all';
  }

  get hasUnread(): boolean {
    return this.notifications.some((n) => !n.isRead);
  }

  get allSelected(): boolean {
    return (
      this.notifications.length > 0 &&
      this.notifications.every((n) => this.selectedIds.includes(n._id))
    );
  }

  toggleSelectAll(checked: boolean): void {
    this.selectedIds = checked ? this.notifications.map((n) => n._id) : [];
  }

  typeConfig(type: string) {
    return getNotificationTypeConfig(type);
  }

  timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60_000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  }
}
